import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from "../../supabaseClient.js";
import { CLIENT_CHAT_SESSION_TYPES } from "../../utils/chatSessionsQuery.js";
import useAlert from '../../alert/useAlert.js';
import { secureUnhash } from '../../utils/patientIdCipher.js';
import { getVitalFlagsFromScan, getSiStatus, isSiCritical, isSiAbnormal } from '../MedicalScribe/Dashboard/mindvaultUtils';
import { getConnectionStatuses } from '../../utils/scribeConnection.js';
import { aggregateDemographics, colorAt } from './clientDemographics.js';

// Shape the demographics card renders before the first fetch lands, so it has
// something valid to read instead of guarding every array.
const EMPTY_DEMOGRAPHICS = { total: 0, gender: [], age: [], conditions: [], diagnosis: [] };

const toISODate = (d) => new Date(d).toISOString();

const hasChinese = (str) => /[\u4e00-\u9fff\u3400-\u4dbf]/.test(str);
const translationCache = {};
const translateToEnglish = async (text) => {
  if (!hasChinese(text)) return text;
  if (translationCache[text]) return translationCache[text];
  try {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=zh-TW|en`
    );
    const json = await res.json();
    const translated = json?.responseData?.translatedText;
    if (translated && json.responseStatus === 200) {
      translationCache[text] = translated;
      return translated;
    }
  } catch (_) {}
  return text;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const dateRangeFromPreset = (preset, customFrom, customTo) => {
  const now = new Date();
  if (preset === 'custom' && customFrom && customTo) {
    return { start: startOfDay(customFrom), end: endOfDay(customTo) };
  }
  if (preset === 'month') {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  if (preset === 'quarter') {
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  // default week
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start: startOfDay(start), end: endOfDay(now) };
};

const daysBetween = (start, end) => {
  const out = [];
  const cur = startOfDay(start);
  const last = startOfDay(end);
  while (cur <= last) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const decryptText = async (value) => {
  if (!value) return '';
  try {
    const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
    if (!error) return data?.result ?? data ?? '';
  } catch (err) {
    console.error('decrypt_text error:', err);
  }
  return value || '';
};

const normalizePhone = (v) => String(v || '').replace(/\D/g, '');

export const useDashboardData = ({ enabled = true } = {}) => {
  const { userDetails } = useSelector(
    (state) => ({ userDetails: state.globalState.userDetails }),
    shallowEqual
  );
  const role = useSelector((state) => state.globalState.role, shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);
  const teamMembersDataRef = useRef([]);

  const [loading, setLoading] = useState(false);

  const [timeRange, setTimeRange] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [teamMembersData, setTeamMembersData] = useState([]);
  const [selectedCaseOwnerFilter, setSelectedCaseOwnerFilter] = useState('ALL');

  const [matchedMindvaultIds, setMatchedMindvaultIds] = useState([]);
  const [activeMindvaultIds, setActiveMindvaultIds] = useState([]);

  const [scanCount, setScanCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [appointmentCount, setAppointmentCount] = useState(0);

  const [scanSeries, setScanSeries] = useState([]);
  const [chatSeries, setChatSeries] = useState([]);
  const [isSingleDaySeries, setIsSingleDaySeries] = useState(false);

  const [riskCounts, setRiskCounts] = useState({ NORMAL: 0, LOW: 0, MEDIUM: 0, HIGH: 0 });
  const [consultationThemes, setConsultationThemes] = useState([]);
  const [demographics, setDemographics] = useState(EMPTY_DEMOGRAPHICS);

  const { start, end } = useMemo(
    () => dateRangeFromPreset(timeRange, customFrom, customTo),
    [timeRange, customFrom, customTo]
  );

  const fetchDashboard = useCallback(async () => {
    if (!enabled || !currentOrg?.id) return;

    setLoading(true);
    try {
      // 1) patients for this team
      const { data: patients, error: patientErr } = await supabase
        .from('patients')
        .select(
          // gender and birth_date are encrypted at rest and cost a decrypt_text
          // round trip each; preExistingConditions and diagnosis are plain text[]
          // and come back usable. patients.age is deliberately NOT selected -- it
          // is written once at creation and is wrong for anyone who has since had
          // a birthday, so the chart derives age from birth_date instead.
          'id, phone_code, phoneNumber, email, case_owner, team_ids, gender, birth_date, "preExistingConditions", diagnosis'
        )
        .eq('teamid', currentOrg.id);

      if (patientErr) throw patientErr;

      const decryptedPatients = await Promise.all(
        (patients || []).map(async (p) => ({
          ...p,
          phone_code: await decryptText(p.phone_code),
          phoneNumber: await decryptText(p.phoneNumber),
          email: await decryptText(p.email),
          gender: await decryptText(p.gender),
          birth_date: await decryptText(p.birth_date),
        }))
      );

      const teamMemberIds = teamMembersDataRef.current.map(m => m.id);
      const caseOwnerFiltered = decryptedPatients.filter((p) => {
        const owners = Array.isArray(p.case_owner) ? p.case_owner : [];
        const patientTeamIds = Array.isArray(p.team_ids) ? p.team_ids : [];
        const inCurrentTeam = patientTeamIds.includes(currentOrg?.id);
        if (selectedCaseOwnerFilter === 'ALL') {
          return owners.some(id => teamMemberIds.includes(id)) && inCurrentTeam;
        }
        return owners.includes(selectedCaseOwnerFilter) && inCurrentTeam;
      });

      // Demographics are a fact about the caseload, so they count every client the
      // case-owner filter left in — NOT `linkedPatients` below, which keeps only the
      // ones who have connected a MindVault account. Charting the linked subset would
      // describe who adopted the app and label it as who the practice sees.
      //
      // Set here, above the early return, for the same reason: a practice with no
      // connected clients still has a caseload, and its demographics should not go
      // blank because nobody signed up.
      setDemographics(aggregateDemographics(caseOwnerFiltered));

      const patientEmailSet = new Set(
        caseOwnerFiltered
          .map((p) => (p.email || '').trim().toLowerCase())
          .filter(Boolean)
      );

      // 2) mindvault accounts — only *connected* clients surface here
      // ((booking OR access code) AND consent, checked server-side)
      const statusByEmail = await getConnectionStatuses(
        userDetails?.id,
        [...patientEmailSet]
      );
      const matched = [...statusByEmail.entries()]
        .filter(([, s]) => s.connected)
        .map(([emailKey, s]) => ({ id: s.mindvaultAccountId, emailKey }));
      const matchedIds = matched.map((a) => a.id);
      setMatchedMindvaultIds(matchedIds);

      // linked patients only
      const linkedEmailSet = new Set(matched.map((m) => m.emailKey));
      const linkedPatients = caseOwnerFiltered.filter((p) =>
        linkedEmailSet.has((p.email || '').trim().toLowerCase())
      );

      if (matchedIds.length === 0) {
        unstable_batchedUpdates(() => {
          setActiveMindvaultIds([]);
          setScanCount(0);
          setChatCount(0);
          setAppointmentCount(0);
          setScanSeries([]);
          setChatSeries([]);
          setIsSingleDaySeries(false);
          setRiskCounts({ NORMAL: 0, LOW: 0, MEDIUM: 0, HIGH: 0 });
          setConsultationThemes([]);
        });
        return;
      }

      // 3) scans in range
      const { data: scans, error: scanErr } = await supabase
        .from('scan_history')
        .select('id, user_id, created_at, heart_rate, sbp, dbp, oxygen_saturation, respiration_rate, stress_index, hr_variability')
        .in('user_id', matchedIds)
        .gte('created_at', toISODate(start))
        .lte('created_at', toISODate(end));

      if (scanErr) throw scanErr;

      // 4) chat sessions in range
      const { data: chats, error: chatErr } = await supabase
        .from('chat_sessions')
        .select('id, user_id, created_at, risk_level, key_themes')
        .in('user_id', matchedIds)
        .eq('is_deleted', false)
        .or(CLIENT_CHAT_SESSION_TYPES)
        .gte('created_at', toISODate(start))
        .lte('created_at', toISODate(end));

      if (chatErr) throw chatErr;

      // Consultation themes from key_themes (each element can be encrypted).
      // Palette lives in clientDemographics.js so this donut and the demographics
      // charts cannot drift to different colours for the same dashboard.
      const decryptedChats = await Promise.all(
        (chats || []).map(async (c) => {
          if (!c.key_themes?.length) return [];
          const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: c.key_themes });
          if (error) return [];
          if (Array.isArray(data)) return data.filter(Boolean);
          if (typeof data === 'string') {
            try { return JSON.parse(data).filter(Boolean); } catch { return []; }
          }
          return [];
        })
      );
      const allThemes = decryptedChats.flat();
      const translatedThemes = await Promise.all(allThemes.map((t) => translateToEnglish(t)));
      const themeCounts = {};
      translatedThemes.forEach((t) => {
        themeCounts[t] = (themeCounts[t] || 0) + 1;
      });
      const themeTotal = Object.values(themeCounts).reduce((a, b) => a + b, 0);
      const aggregatedThemes = Object.entries(themeCounts)
        .sort(([, a], [, b]) => b - a)
        .map(([name, count], idx) => ({
          name,
          value: themeTotal > 0 ? Math.round((count / themeTotal) * 100) : 0,
          color: colorAt(idx),
        }));

      // 5) active users
      const activeSet = new Set([
        ...(scans || []).map((s) => s.user_id),
        ...(chats || []).map((c) => c.user_id),
      ]);

      // 6) appointments in range (team filter + matching phone id)
      const { data: appts, error: apptErr } = await supabase
        .from('appointments')
        .select('id, appointment_date, patient_id')
        .eq('team_id', currentOrg.id)
        .gte('appointment_date', toISODate(start))
        .lte('appointment_date', toISODate(end));

      if (apptErr) throw apptErr;

      const decryptedAppts = await Promise.all(
        (appts || []).map(async (a) => {
          const decryptedPatientId = await decryptText(a.patient_id);
          let unhashed = decryptedPatientId;
          try {
            unhashed = await secureUnhash(
              decryptedPatientId,
              process.env.REACT_APP_PATIENT_ID_SECRET
            );
          } catch {
            // fallback keep decrypted
          }
          return { ...a, unhashedPatientId: unhashed };
        })
      );

      const mvPhoneSet = new Set(
        linkedPatients.map((p) => normalizePhone(`${p.phone_code || ''}${p.phoneNumber || ''}`)).filter(Boolean)
      );

      const matchingAppointments = decryptedAppts.filter((a) =>
        mvPhoneSet.has(normalizePhone(a.unhashedPatientId))
      );

      // 7) line chart series
      const isSingleDay = startOfDay(start).getTime() === startOfDay(end).getTime();
      let finalSeries;

      if (isSingleDay) {
        const byMinute = new Map();

        (scans || []).forEach((s) => {
          const d = new Date(s.created_at);
          const minute = d.getHours() * 60 + d.getMinutes();
          const timeLabel = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
          if (!byMinute.has(minute)) byMinute.set(minute, { minute, timeLabel, scans: 0, chats: 0 });
          byMinute.get(minute).scans += 1;
        });

        (chats || []).forEach((c) => {
          const d = new Date(c.created_at);
          const minute = d.getHours() * 60 + d.getMinutes();
          const timeLabel = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
          if (!byMinute.has(minute)) byMinute.set(minute, { minute, timeLabel, scans: 0, chats: 0 });
          byMinute.get(minute).chats += 1;
        });

        finalSeries = Array.from(byMinute.values()).sort((a, b) => a.minute - b.minute);
      } else {
        const days = daysBetween(start, end);
        const scanCountsByDay = new Map();
        const chatCountsByDay = new Map();

        (scans || []).forEach((s) => {
          const k = new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
          scanCountsByDay.set(k, (scanCountsByDay.get(k) || 0) + 1);
        });

        (chats || []).forEach((c) => {
          const k = new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
          chatCountsByDay.set(k, (chatCountsByDay.get(k) || 0) + 1);
        });

        finalSeries = days.map((d) => {
          const k = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
          return {
            day: k,
            scans: scanCountsByDay.get(k) || 0,
            chats: chatCountsByDay.get(k) || 0,
          };
        });
      }

      // 8) risk distribution - one count per user at their highest risk level.
      const counts = { NORMAL: 0, LOW: 0, MEDIUM: 0, HIGH: 0 };

      const userLatestChat = new Map();
      (chats || []).forEach((c) => {
        const existing = userLatestChat.get(c.user_id);
        if (!existing || new Date(c.created_at) > new Date(existing.created_at)) {
          userLatestChat.set(c.user_id, c);
        }
      });

      const userLatestScan = new Map();
      (scans || []).forEach((s) => {
        const existing = userLatestScan.get(s.user_id);
        if (!existing || new Date(s.created_at) > new Date(existing.created_at)) {
          userLatestScan.set(s.user_id, s);
        }
      });

      const allUserIds = new Set([...userLatestChat.keys(), ...userLatestScan.keys()]);
      allUserIds.forEach((userId) => {
        let userRisk = 'NORMAL';

        const chat = userLatestChat.get(userId);
        if (chat) {
          const lvl = String(chat.risk_level || '').toUpperCase();
          if (lvl === 'HIGH') userRisk = 'HIGH';
          else if (lvl === 'MEDIUM') userRisk = 'MEDIUM';
          else if (lvl === 'LOW' && userRisk === 'NORMAL') userRisk = 'LOW';
        }

        const scan = userLatestScan.get(userId);
        if (scan && userRisk !== 'HIGH') {
          const flags = getVitalFlagsFromScan(scan);
          const stress = getSiStatus(scan.stress_index);
          const hasCritical = (flags?.critical?.length > 0) || isSiCritical(stress);
          const hasAbnormal = (flags?.abnormal?.length > 0) || isSiAbnormal(stress);
          if (hasCritical) userRisk = 'HIGH';
          else if (hasAbnormal) userRisk = 'MEDIUM';
        }

        counts[userRisk] += 1;
      });

      // Flush all metric state in one render pass
      unstable_batchedUpdates(() => {
        setScanCount(scans?.length || 0);
        setChatCount(chats?.length || 0);
        setConsultationThemes(aggregatedThemes);
        setActiveMindvaultIds([...activeSet]);
        setAppointmentCount(matchingAppointments.length);
        setScanSeries(finalSeries);
        setChatSeries(finalSeries);
        setIsSingleDaySeries(isSingleDay);
        setRiskCounts(counts);
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setAlert('Failed to load dashboard data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [enabled, currentOrg, start, end, setAlert, selectedCaseOwnerFilter, userDetails?.id]);

  const getTeamMembers = useCallback(async () => {
    if (!currentOrg) return;
    const { data: memberData } = await supabase
      .from('team_users')
      .select('user_id, role')
      .eq('team_id', currentOrg.id);

    if (!memberData?.length) { teamMembersDataRef.current = []; setTeamMembersData([]); return; }

    const roleByUserId = Object.fromEntries(memberData.map(m => [m.user_id, m.role]));
    const teamMemberIds = memberData.map(m => m.user_id);

    const { data: usersData } = await supabase
      .from('user_accounts')
      .select('id, name')
      .in('id', teamMemberIds);

    const decryptedUsers = await Promise.all(
      (usersData || []).map(async (u) => {
        const name = await decryptText(u.name);
        return { id: u.id, name, role: roleByUserId[u.id] ?? null };
      })
    );
    teamMembersDataRef.current = decryptedUsers;
    setTeamMembersData(decryptedUsers);
  }, [currentOrg]);

  const currentUserRole = teamMembersData.find(m => m.id === userDetails?.id)?.role ?? null;

  useEffect(() => {
    if (currentUserRole && currentUserRole !== 'Admin' && userDetails?.id) {
      setSelectedCaseOwnerFilter(userDetails.id);
    }
  }, [currentUserRole, userDetails?.id]);

  useEffect(() => {
    if (!enabled) return;
    if (!hasMounted.current && currentOrg) {
      fetchDashboard();
      getTeamMembers();
      hasMounted.current = true;
    }
  }, [enabled, currentOrg, fetchDashboard, getTeamMembers]);

  useEffect(() => {
    if (enabled && currentOrg && hasMounted.current) {
      if (timeRange === 'custom' && (!customFrom || !customTo)) return;
      fetchDashboard();
      getTeamMembers();
    }
  }, [enabled, timeRange, customFrom, customTo, currentOrg, fetchDashboard, getTeamMembers]);

  return {
    loading,
    timeRange,
    setTimeRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,

    matchedMindvaultIds,
    activeMindvaultIds,

    scanCount,
    chatCount,
    appointmentCount,
    scanSeries,
    chatSeries,
    isSingleDaySeries,
    riskCounts,
    consultationThemes,
    demographics,

    role,
    userDetails,
    currentOrg,

    teamMembersData,
    selectedCaseOwnerFilter,
    setSelectedCaseOwnerFilter,
    currentUserRole,
  };
};

export default useDashboardData;